import type { CrossHairMovedEventParams, IChartWidgetApi } from "@charting_library";

// Module-level state is intentional: the app only renders one chart at a time.
// If multiple simultaneous charts are ever needed, refactor to a Map<chartId, data>.
let lastCrosshairData: CrossHairMovedEventParams | null = null;

/**
 * Initialize crosshair tracking on the chart
 * Stores the latest crosshair position for telemetry retrieval
 * @returns Unsubscribe function to clean up the subscription
 */
export function initCrosshairTracking(chart: IChartWidgetApi): () => void {
  // Define callback so we can reference it for unsubscribe
  const onCrosshairMoved = (params: CrossHairMovedEventParams) => {
    lastCrosshairData = {
      time: params.time, // UNIX timestamp (UTC)
      userTime: params.userTime, // User timezone adjusted
      price: params.price,
      offsetX: params.offsetX,
      offsetY: params.offsetY,
      entityValues: params.entityValues, // Study/indicator values
    };
  };

  // Subscribe to crosshair movements
  const subscription = chart.crossHairMoved();
  subscription.subscribe(null, onCrosshairMoved);

  // Return unsubscribe function
  return () => subscription.unsubscribe(null, onCrosshairMoved);
}

/**
 * Get the current crosshair data
 * Returns null if crosshair has not been moved yet
 */
export function getCurrentCrosshairData(): CrossHairMovedEventParams | null {
  return lastCrosshairData;
}

/**
 * Get a time range centered on the crosshair position
 * @param bufferMinutes - Minutes to buffer on each side of the crosshair time
 * @returns Time range object with 'from' and 'to' timestamps, or null if no crosshair data
 */
export function getCrosshairTimeRange(
  bufferMinutes: number = 30,
): { from: number; to: number } | null {
  if (!lastCrosshairData || !lastCrosshairData.time) {
    return null;
  }

  const centerTime = lastCrosshairData.time;
  const bufferSeconds = bufferMinutes * 60;

  return {
    from: centerTime - bufferSeconds,
    to: centerTime + bufferSeconds,
  };
}

/**
 * Reset crosshair tracking data
 * Useful when chart is destroyed or reset
 */
export function resetCrosshairTracking(): void {
  lastCrosshairData = null;
}
